import type { NotificationResponse } from '@monorepo/notification-common';
import {
  ClientApplicationConfig,
  Credit,
  CreditTransaction,
  FooterIcon,
  FooterMenuConfig,
  HeaderMenuLinkAdmin,
  I18nEntryArray,
  MenuLink,
  UserTermsAndConditionsConfig,
  YoutubeLiveStream,
} from '@monorepo/types';
import apiFetch from '../api-client';

export const fetchNotifications = async (): Promise<NotificationResponse[]> => {
  return apiFetch('/v2/notifications');
};

export const fetchNotificationHistory = async (): Promise<
  NotificationResponse[]
> => {
  return apiFetch('/v2/notifications/history');
};

export const fetchNotificationsStatus = async (): Promise<{
  unread: number;
}> => {
  return apiFetch('/v2/notifications/status');
};

export const markNotificationAsRead = async (
  notificationId: string,
): Promise<void> => {
  await apiFetch(`/v2/notifications/mark-as-read/${notificationId}`, {
    method: 'PUT',
  });
};

export const markAllNotificationsAsRead = async (): Promise<void> => {
  await apiFetch('/v2/notifications/mark-all-as-read', {
    method: 'PUT',
  });
};

export const fetchUserCreditHistory = async (): Promise<{
  transactions: CreditTransaction[];
  credits: Credit[];
}> => {
  const response = await apiFetch('/credits/credits-history');

  // Convert string dates to Date objects for credits
  const credits = response.credits.map((credit: Record<string, unknown>) => ({
    ...credit,
    expirationDate: new Date(credit.expirationDate as string),
    createdAt: new Date(credit.createdAt as string),
    updatedAt: new Date(credit.updatedAt as string),
  }));

  // Convert string dates to Date objects for transactions
  const transactions = response.transactions.map(
    (transaction: Record<string, unknown>) => ({
      ...transaction,
      createdAt: new Date(transaction.createdAt as string),
      updatedAt: new Date(transaction.updatedAt as string),
    }),
  );

  return {
    transactions,
    credits,
  };
};

export const fetchYoutubeLiveStreamVideos =
  async (): Promise<YoutubeLiveStream> => {
    return apiFetch('/google-services/youtube/live-stream-videos');
  };

export const fetchHeaderMenu = async (): Promise<HeaderMenuLinkAdmin[]> => {
  return apiFetch('/application-config/header-menu-links');
};

export const fetchFooterConfig = async (): Promise<FooterMenuConfig> => {
  return apiFetch('/application-config/footer-config');
};

export interface FooterConfigResponse {
  links: MenuLink[];
  icons: FooterIcon[];
}

export const fetchApplicationConfig =
  async (): Promise<ClientApplicationConfig> => {
    return apiFetch('/application-config');
  };

export const fetchUserTermsAndConditions =
  async (): Promise<UserTermsAndConditionsConfig> => {
    return apiFetch('/application-config/user-terms-and-conditions');
  };

export const fetchMenuTranslations = async (): Promise<I18nEntryArray> => {
  return apiFetch('/application-config/menu-translations');
};

export const updateUserLanguage = async (
  languageCode: string,
): Promise<void> => {
  await apiFetch('/user', {
    method: 'PUT',
    body: JSON.stringify({ applicationLanguage: languageCode }),
    headers: {
      'Content-Type': 'application/json',
    },
  });
};
